import { useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { clearAuthState, mfaClaimAtom, tokenAtom } from "../store/authAtoms";
import PageLoading from "./PageLoading";
import apiClient from "../api/apiClient";
import { useSetAtom } from "jotai";
import {
  extractMfaClaim,
  getTokenClaims,
  resolveMfaClaimFromStatus,
} from "../utils/authStorage";

type MfaStatusResponse = {
  required: boolean;
  enrolled: boolean;
  phase: string;
};

const isAuthFailure = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
};

const isMissingOrganization = (error: unknown): boolean => {
  const response = (
    error as {
      response?: { status?: number; data?: { detail?: { code?: string } } };
    }
  )?.response;
  return (
    response?.status === 404 &&
    response?.data?.detail?.code === "ORG_NOT_FOUND"
  );
};

/**
 * PersistLogin ensures auth state is initialized and token is revalidated before rendering protected routes.
 * When a token exists but is expired or near expiry, we refresh once before rendering so the first request
 * doesn't trigger a 401 and so returning users are revalidated regardless of which page or query runs first.
 */
const PersistLogin = () => {
  const setToken = useSetAtom(tokenAtom);
  const setMfaClaim = useSetAtom(mfaClaimAtom);
  const [ready, setReady] = useState(typeof window === "undefined");
  const [loadError, setLoadError] = useState(false);

  const initializeAuth = useCallback(async () => {
    setReady(false);
    setLoadError(false);
    try {
      await apiClient.get("/user/info");

      const storedToken =
        typeof window !== "undefined" ? localStorage.getItem("token") : null;
      const tokenClaims = storedToken ? getTokenClaims(storedToken) : null;
      const tokenMfaClaim = tokenClaims ? extractMfaClaim(tokenClaims) : null;

      try {
        const { data } = await apiClient.get<MfaStatusResponse>(
          "/auth/mfa/status",
        );
        setMfaClaim(resolveMfaClaimFromStatus(data));
      } catch (error) {
        if (isMissingOrganization(error)) {
          setMfaClaim(tokenMfaClaim ?? "not_required");
        } else {
          throw error;
        }
      }

      setToken(storedToken ?? "cookie");
      setReady(true);
    } catch (error) {
      if (isAuthFailure(error)) {
        await clearAuthState();
        window.location.href = "/login";
        return;
      }
      setLoadError(true);
    }
  }, [setMfaClaim, setToken]);

  useEffect(() => {
    void initializeAuth();
  }, [initializeAuth]);

  if (!ready) {
    if (loadError) {
      return (
        <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-gray-600">
            Something went wrong checking your session.
          </p>
          <button
            type="button"
            onClick={() => void initializeAuth()}
            className="rounded-full bg-orange-500 px-6 py-2.5 font-semibold text-white hover:bg-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
          >
            Try again
          </button>
        </div>
      );
    }
    return <PageLoading />;
  }

  return <Outlet />;
};

export default PersistLogin;
